'use client';
import { useState } from 'react';

import { type NormalizedError, normalizeError } from '../../lib/normalize-error';
import { useToastContext } from '../shared/Toast/ToastProvider';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

type ProfileData = {
    id: string;
    name: string;
    email: string;
    bio: string;
    website: string;
    timezone: string;
    language: string;
    displayName: string;
    pronouns: string;
    interests: string[];
    emailNotifications: boolean;
    pushNotifications: boolean;
    marketingEmails: boolean;
    profileVisibility: 'PUBLIC' | 'PRIVATE' | 'FRIENDS';
    profileCompleted: boolean;
};
type ProfileEditFormProps = {
    initialData: ProfileData;
    onUpdate: (data: Partial<ProfileData>) => void;
};
const TIMEZONES = [
    'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Rome', 'Asia/Tokyo',
    'Asia/Shanghai', 'Asia/Kolkata', 'Australia/Sydney', 'Pacific/Auckland'
];
const LANGUAGES = [
    'en', 'es', 'fr', 'de', 'it', 'pt', 'ru', 'ja', 'ko', 'zh', 'ar', 'hi'
];
const INTEREST_OPTIONS = [
    'Technology', 'Design', 'Business', 'Science', 'Art', 'Music', 'Sports',
    'Travel', 'Food', 'Fitness', 'Gaming', 'Reading', 'Photography', 'Writing',
    'Programming', 'AI/ML', 'Data Science', 'Web Development', 'Mobile Development'
];
export function ProfileEditForm({ initialData, onUpdate }: ProfileEditFormProps) {
    const { toast } = useToastContext();
    const [formData, setFormData] = useState<ProfileData>(initialData);
    const [isLoading, setIsLoading] = useState(false);
    const [interestInput, setInterestInput] = useState('');
    const handleInputChange = (field: keyof ProfileData, value: unknown) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };
    const handleAddInterest = () => {
        if (interestInput.trim() && !formData.interests.includes(interestInput.trim())) {
            const modifiedInterests = [...formData.interests, interestInput.trim()];
            handleInputChange('interests', modifiedInterests);
            setInterestInput('');
        }
    };
    const handleRemoveInterest = (interest: string) => {
        const modifiedInterests = formData.interests.filter(i => i !== interest);
        handleInputChange('interests', modifiedInterests);
    };
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const response = await fetch('/api/profile', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });
            const result = (await response.json()) as {
                user?: Partial<ProfileData>;
                error?: string;
            };
            if (response.ok) {
                if (result.user) {
                    onUpdate(result.user);
                }
                toast({
                    title: 'Profile updated successfully',
                    description: 'Your profile has been saved.',
                });
            }
            else {
                throw new Error(result.error || 'Failed to update profile');
            }
        }
        catch (error: unknown) {
            const errorNormalized: NormalizedError = normalizeError(error);
            toast({
                title: 'Error updating profile',
                description: errorNormalized.message,
                variant: 'destructive',
            });
        }
        finally {
            setIsLoading(false);
        }
    };
    const calculateProfileCompletion = () => {
        const fields = ['name', 'bio', 'website', 'timezone', 'language', 'displayName', 'pronouns', 'interests'];
        const completed = fields.filter(field => {
            const value = formData[field as keyof ProfileData];
            return value !== null && value !== undefined && value !== '' &&
                (Array.isArray(value) ? value.length > 0 : true);
        }).length;
        return Math.round((completed / fields.length) * 100);
    };
    return (<form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-6">
      {/* Profile Completion Progress */}
      <div className="bg-background-muted border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-foreground-default">Profile Completion</h3>
          <span className="text-sm text-foreground-muted">{calculateProfileCompletion()}%</span>
        </div>
        <div className="w-full bg-border rounded-full h-2">
          <div className="bg-primary h-2 rounded-full transition-all duration-300" style={{ width: `${calculateProfileCompletion()}%` }}/>
        </div>
        <p className="text-xs text-foreground-muted mt-1">
          Complete your profile to unlock all features
        </p>
      </div>

      {/* Basic Information */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-foreground-default mb-2">
            Display Name *
          </label>
          <Input value={formData.displayName !== null && formData.displayName !== undefined ? formData.displayName : ''} onChange={(e) => handleInputChange('displayName', e.target.value)} placeholder="How you'd like to be called" maxLength={50}/>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-default mb-2">
            Pronouns
          </label>
          <Input value={formData.pronouns !== null && formData.pronouns !== undefined ? formData.pronouns : ''} onChange={(e) => handleInputChange('pronouns', e.target.value)} placeholder="e.g., they/them, she/her, he/him" maxLength={20}/>
        </div>

        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-foreground-default mb-2">
            Bio
          </label>
          <textarea value={formData.bio !== null && formData.bio !== undefined ? formData.bio : ''} onChange={(e) => handleInputChange('bio', e.target.value)} placeholder="Tell us about yourself..." maxLength={500} rows={3} className="block w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-primary-default focus:border-primary-default sm:text-sm bg-background-secondary text-foreground-default"/>
          <p className="text-xs text-foreground-muted mt-1">
            {formData.bio?.length || 0}/500 characters
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-default mb-2">
            Website
          </label>
          <Input type="url" value={formData.website !== null && formData.website !== undefined ? formData.website : ''} onChange={(e) => handleInputChange('website', e.target.value)} placeholder="https://yourwebsite.com"/>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-default mb-2">
            Timezone
          </label>
          <select value={formData.timezone !== null && formData.timezone !== undefined ? formData.timezone : ''} onChange={(e) => handleInputChange('timezone', e.target.value)} className="block w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-primary-default focus:border-primary-default sm:text-sm bg-background-secondary text-foreground-default">
            <option value="">Select timezone</option>
            {TIMEZONES.map(tz => (<option key={tz} value={tz}>{tz}</option>))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-default mb-2">
            Language
          </label>
          <select value={formData.language !== null && formData.language !== undefined ? formData.language : ''} onChange={(e) => handleInputChange('language', e.target.value)} className="block w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-primary-default focus:border-primary-default sm:text-sm bg-background-secondary text-foreground-default">
            <option value="">Select language</option>
            {LANGUAGES.map(lang => (<option key={lang} value={lang}>{lang.toUpperCase()}</option>))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-default mb-2">
            Profile Visibility
          </label>
          <select value={formData.profileVisibility} onChange={(e) => handleInputChange('profileVisibility', e.target.value)} className="block w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-primary-default focus:border-primary-default sm:text-sm bg-background-secondary text-foreground-default">
            <option value="PRIVATE">Private</option>
            <option value="FRIENDS">Friends Only</option>
            <option value="PUBLIC">Public</option>
          </select>
        </div>
      </div>

      {/* Interests */}
      <div>
        <label className="block text-sm font-medium text-foreground-default mb-2">
          Interests
        </label>
        <div className="flex flex-wrap gap-2 mb-3">
          {formData.interests.map((interest, index) => (<span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-background-muted text-foreground-default">
              {interest}
              <button type="button" onClick={() => handleRemoveInterest(interest)} className="ml-1 text-foreground-muted hover:text-foreground-default">
                ×
              </button>
            </span>))}
        </div>
        <div className="flex gap-2">
          <Input value={interestInput} onChange={(e) => setInterestInput(e.target.value)} placeholder="Add an interest..." onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddInterest())}/>
          <Button type="button" onClick={handleAddInterest} disabled={!interestInput.trim()} variant="outline">
            Add
          </Button>
        </div>
        <div className="flex flex-wrap gap-1 mt-2">
          {INTEREST_OPTIONS.filter(option => !formData.interests.includes(option) &&
            option.toLowerCase().includes(interestInput.toLowerCase())).slice(0, 5).map(option => (<button key={option} type="button" onClick={() => {
                setInterestInput(option);
                handleAddInterest();
            }} className="text-xs px-2 py-1 bg-background-muted hover:bg-background-default rounded text-foreground-default">
              {option}
            </button>))}
        </div>
      </div>

      {/* Notification Preferences */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-medium text-foreground-default mb-4">Notification Preferences</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-foreground-default">Email Notifications</label>
              <p className="text-xs text-foreground-muted">Receive important updates via email</p>
            </div>
            <input type="checkbox" checked={formData.emailNotifications} onChange={(e) => handleInputChange('emailNotifications', e.target.checked)} className="h-4 w-4 text-primary-default focus:ring-primary-default border-border rounded"/>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-foreground-default">Push Notifications</label>
              <p className="text-xs text-foreground-muted">Receive notifications in your browser</p>
            </div>
            <input type="checkbox" checked={formData.pushNotifications} onChange={(e) => handleInputChange('pushNotifications', e.target.checked)} className="h-4 w-4 text-primary-default focus:ring-primary-default border-border rounded"/>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-foreground-default">Marketing Emails</label>
              <p className="text-xs text-foreground-muted">Receive product updates and tips</p>
            </div>
            <input type="checkbox" checked={formData.marketingEmails} onChange={(e) => handleInputChange('marketingEmails', e.target.checked)} className="h-4 w-4 text-primary-default focus:ring-primary-default border-border rounded"/>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end space-x-3 pt-6 border-t">
        <Button type="button" variant="outline" onClick={() => setFormData(initialData)} disabled={isLoading}>
          Reset
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>);
}
